// src/lib/notificationtrigger.ts
import { supabase } from './supabase';
import { createNotification } from './notifications';
import { getCurrentUser } from './auth';
import { 
  sendFriendAcceptedPush, 
  sendNewSongPush,
  sendNewStoryPush 
} from './onesignal';

/** Small helper to chunk large arrays to avoid flooding RPC requests */
function chunk<T>(arr: T[], size = 50): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Create notification when a new message is received
 * Note: Push notification is sent by the SENDER directly in Messages.tsx
 *       to ensure delivery even when the receiver is offline.
 */
export const notifyNewMessage = async (
  receiverId: string,
  senderId: string,
  senderName: string
): Promise<void> => {
  try {
    // Create in-app notification only (push is handled by sender)
    await createNotification(
      receiverId,
      'message',
      'New Message',
      `You received a new message from ${senderName}`,
      undefined,
      senderId
    );
  } catch (error) {
    console.error('Error creating message notification:', error);
  }
};

/**
 * Create notification when a friend request is received
 * Note: Push notification is sent by the REQUESTER directly in Learners.tsx
 */
export const notifyFriendRequest = async (
  addresseeId: string,
  requesterId: string,
  requesterName: string
): Promise<void> => {
  try {
    // Create in-app notification only (push is handled by requester)
    await createNotification(
      addresseeId,
      'friend_request',
      'New Friend Request',
      `${requesterName} sent you a friend request`,
      undefined,
      requesterId
    );
  } catch (error) {
    console.error('Error creating friend request notification:', error);
  }
};

/**
 * Create notification when a friend request is accepted
 * Note: Push notification is sent by the ACCEPTER directly in Learners.tsx
 */
export const notifyFriendAccepted = async (
  requesterId: string,
  addresseeId: string,
  addresseeName: string
): Promise<void> => {
  try {
    // Create in-app notification + send push (called from action initiator)
    await createNotification(
      requesterId,
      'friend_accepted',
      'Friend Request Accepted',
      `${addresseeName} accepted your friend request`,
      undefined,
      addresseeId
    );
    
    // Send push notification
    await sendFriendAcceptedPush(requesterId, addresseeName);
  } catch (error) {
    console.error('Error creating friend accepted notification:', error);
  }
};

/**
 * Create notification for new song added
 */
export const notifyNewSong = async (
  songId: string,
  songTitle: string
): Promise<void> => {
  try {
    // Get all users who have access (admin or active coupon)
    const { data: adminUsers, error: adminErr } = await supabase
      .from('stripeusers')
      .select('id')
      .eq('is_admin', true);

    if (adminErr) throw adminErr;

    const { data: couponUsers, error: couponErr } = await supabase
      .from('stripecouponusage')
      .select('user_id')
      .gte('valid_until', new Date().toISOString());

    if (couponErr) throw couponErr;

    // Unique user ids
    const allUserIds = new Set<string>([
      ...((adminUsers || []).map((u: any) => u.id)),
      ...((couponUsers || []).map((u: any) => u.user_id)),
    ]);

    const userIdsArray = Array.from(allUserIds);

    // Create in-app notifications in chunks
    for (const group of chunk(userIdsArray, 50)) {
      await Promise.allSettled(
        group.map((userId) =>
          createNotification(
            userId,
            'new_song',
            'New Song Available',
            `New song "${songTitle}" has been added to the platform`,
            songId
          )
        )
      );
    }

    // Send push notifications (OneSignal handles batching)
    if (userIdsArray.length > 0) {
      await sendNewSongPush(userIdsArray, songTitle, songId);
    }
  } catch (error) {
    console.error('Error creating new song notifications:', error);
  }
};

/**
 * Create notification for new story added
 */
export const notifyNewStory = async (
  storyId: string,
  storyTitle: string
): Promise<void> => {
  try {
    // Get all users who have access (admin or active coupon)
    const { data: adminUsers, error: adminErr } = await supabase
      .from('stripeusers')
      .select('id')
      .eq('is_admin', true);

    if (adminErr) throw adminErr;

    const { data: couponUsers, error: couponErr } = await supabase
      .from('stripecouponusage')
      .select('user_id')
      .gte('valid_until', new Date().toISOString());

    if (couponErr) throw couponErr;

    // Unique user ids
    const allUserIds = new Set<string>([
      ...((adminUsers || []).map((u: any) => u.id)),
      ...((couponUsers || []).map((u: any) => u.user_id)),
    ]);

    const userIdsArray = Array.from(allUserIds);

    // Create in-app notifications in chunks
    for (const group of chunk(userIdsArray, 50)) {
      await Promise.allSettled(
        group.map((userId) =>
          createNotification(
            userId,
            'new_story',
            'New Story Available',
            `New story "${storyTitle}" has been added to the platform`,
            storyId
          )
        )
      );
    }

    // Send push notifications (OneSignal handles batching)
    if (userIdsArray.length > 0) {
      await sendNewStoryPush(userIdsArray, storyTitle, storyId);
    }
  } catch (error) {
    console.error('Error creating new story notifications:', error);
  }
};

/**
 * Setup real-time triggers for automatic notifications.
 * Returns a cleanup function to unsubscribe.
 */
export const setupNotificationTriggers = () => {
  const user = getCurrentUser();
  if (!user?.id) return () => {};

  // Use ONE channel for all events (lighter + easier to clean up)
  const channel = supabase
    .channel(`notifications-hub-${user.id}`)
    // New private message to me
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'private_messages',
        filter: `receiver_id=eq.${user.id}`,
      },
      async (payload) => {
        const message = payload.new as {
          sender_id: string;
          receiver_id: string;
          id: string;
        };

        // Get sender info
        const { data: sender } = await supabase
          .from('stripeusers')
          .select('email, full_name, username')
          .eq('id', message.sender_id)
          .single();

        if (sender) {
          const senderName = sender.full_name || sender.username || sender.email;
          await notifyNewMessage(user.id, message.sender_id, senderName);
        }
      }
    )
    // New friend request to me
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'user_friendships',
        filter: `addressee_id=eq.${user.id}`,
      },
      async (payload) => {
        const friendship = payload.new as {
          requester_id: string;
          addressee_id: string;
        };

        const { data: requester } = await supabase
          .from('stripeusers')
          .select('email, full_name, username')
          .eq('id', friendship.requester_id)
          .single();

        if (requester) {
          const requesterName = requester.full_name || requester.username || requester.email;
          await notifyFriendRequest(user.id, friendship.requester_id, requesterName);
        }
      }
    )
    // My outgoing request got accepted
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_friendships',
        filter: `requester_id=eq.${user.id}`,
      },
      async (payload) => {
        const friendship = payload.new as {
          requester_id: string;
          addressee_id: string;
          status: string;
        };

        if (friendship.status === 'accepted') {
          const { data: addressee } = await supabase
            .from('stripeusers')
            .select('email, full_name, username')
            .eq('id', friendship.addressee_id)
            .single();

          if (addressee) {
            const addresseeName = addressee.full_name || addressee.username || addressee.email;
            await notifyFriendAccepted(user.id, friendship.addressee_id, addresseeName);
          }
        }
      }
    )
    .subscribe();

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch (e) {
      console.warn('Error removing notifications channel:', e);
    }
  };
};
